import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import LockOutlineRoundedIcon from "@mui/icons-material/LockOutlineRounded";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { AuthCardIntro } from "./AuthCardHeader";
import { useConfirmForgotPassword } from "../mutations";
import { PasswordStrengthIndicator } from "./PasswordStrengthIndicator";
import {
  getPasswordRequirements,
  isPasswordValid,
} from "../utils/passwordValidation";

interface ResetPasswordCodeVerificationProps {
  username: string;
  onSuccess: () => void;
  onError: (error: string) => void;
  onBack: () => void;
}

export const ResetPasswordCodeVerification: React.FC<
  ResetPasswordCodeVerificationProps
> = ({ username, onSuccess, onError, onBack }) => {
  const [verificationCode, setVerificationCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touched, setTouched] = useState(false);

  const confirmForgotPasswordMutation = useConfirmForgotPassword();

  const passwordRequirements = useMemo(() => {
    return getPasswordRequirements(password, confirmPassword);
  }, [password, confirmPassword]);

  const isPasswordValidCheck = isPasswordValid(passwordRequirements);
  const isCodeValid = /^\d{6}$/.test(verificationCode.trim());
  const showPasswordError = touched && !isPasswordValidCheck;
  const showCodeError = touched && !isCodeValid;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTouched(true);

    if (!isCodeValid || !isPasswordValidCheck) {
      return;
    }

    try {
      await confirmForgotPasswordMutation.mutateAsync({
        username,
        newPassword: password,
        verificationCode: verificationCode.trim(),
      });
      onSuccess();
    } catch (error) {
      onError(
        error instanceof Error ? error.message : "Failed to reset password"
      );
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          p: 4,
        }}
      >
        <Stack spacing={4}>
          <AuthCardIntro
            title="Reset your password"
            subtitle={`Enter the verification code sent to ${username} and create a new password.`}
          />

          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Verification code"
              value={verificationCode}
              onChange={(event) => setVerificationCode(event.target.value)}
              onBlur={() => setTouched(true)}
              error={showCodeError}
              placeholder="Enter 6-digit code"
              helperText={
                showCodeError
                  ? "Enter the 6-digit code sent to your email."
                  : ""
              }
              inputProps={{
                inputMode: "numeric",
                pattern: "\\d*",
                maxLength: 6,
              }}
              required
            />
            <TextField
              fullWidth
              label="New password"
              value={password}
              type={showPassword ? "text" : "password"}
              onChange={(event) => setPassword(event.target.value)}
              onBlur={() => setTouched(true)}
              error={showPasswordError}
              placeholder="Enter your new password"
              helperText={
                showPasswordError
                  ? "Password must meet all requirements below."
                  : ""
              }
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlineRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Confirm password"
              value={confirmPassword}
              type={showConfirmPassword ? "text" : "password"}
              onChange={(event) => setConfirmPassword(event.target.value)}
              onBlur={() => setTouched(true)}
              error={
                touched &&
                !passwordRequirements.find(
                  (req) => req.key === "passwordsMatch"
                )?.isValid
              }
              placeholder="Confirm your new password"
              helperText={
                touched &&
                !passwordRequirements.find(
                  (req) => req.key === "passwordsMatch"
                )?.isValid
                  ? "Passwords do not match."
                  : ""
              }
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlineRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={handleToggleConfirmPasswordVisibility}
                      edge="end"
                    >
                      {showConfirmPassword ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          {password && (
            <PasswordStrengthIndicator
              requirements={passwordRequirements}
              showProgress={true}
            />
          )}

          <Stack spacing={2}>
            <Button
              type="submit"
              size="large"
              variant="contained"
              fullWidth
              endIcon={<ArrowForwardRoundedIcon />}
              disabled={
                !verificationCode.length ||
                !password.length ||
                !confirmPassword.length ||
                !isPasswordValidCheck ||
                confirmForgotPasswordMutation.isPending
              }
              sx={{ mt: 4, mb: 2 }}
            >
              {confirmForgotPasswordMutation.isPending
                ? "Resetting password..."
                : "Reset Password"}
            </Button>
            <Button
              type="button"
              size="large"
              variant="outlined"
              fullWidth
              onClick={onBack}
              disabled={confirmForgotPasswordMutation.isPending}
            >
              Back to email
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};
